import {ActionTree} from 'vuex';
import {PositionObfuscator, Purchases, UserState, UserTransaction, MAFUser} from './types';
import {Lookup, RootState} from '@/store/types';
import Vue from 'vue';

import * as firebase from 'firebase';
import DocumentSnapshot = firebase.firestore.DocumentSnapshot;
import * as _ from 'lodash';
import QuerySnapshot = firebase.firestore.QuerySnapshot;
import QueryDocumentSnapshot = firebase.firestore.QueryDocumentSnapshot;

/* tslint:disable:no-console */

export const actions: ActionTree<UserState, RootState> = {

    async updateSelfDoc({state}, body: object | undefined): Promise<any> {
        if (body && state.selfRef) {
            try {
                await state.selfRef.update(body);
                return true;
            } catch (e) {
                console.error('Error updating self: ', e);
                throw new Error(e);
            }
        }
    },

    // UPDATE - selected user (admin dashboard)
    async updateUser({ state, commit }, user: MAFUser): Promise<any> {
      if ( user.Reference ) {
          try {
          // update FireStore document reference with new user information
          return await user.Reference.update(user.toPlainObj());
        } catch (error) {
          console.log(error);
          return error;
        }
      } else {
          // user may be coming from the dashboard - try to get document reference
          // UPDATE: This doesnt actually set the reference.
          try {
            Vue.prototype.$firestore.collection('users').
              where('uid', '==', user.uid).onSnapshot(async (snapshot: QuerySnapshot) => {
                // found the user
                if (snapshot.docs.length > 0) {
                  const doc = snapshot.docs[0];
                  const mafUser = new MAFUser(doc.data());
                  return mafUser.setDocumentReference(doc.ref);
                } else {
                  console.error('User not found');
                }
              }, (error: any) => {
                if (Vue.prototype.$auth.currentUser !== null) {
                    console.log(`Error getting user: ${error}`);
                }
              });
          } catch (error) {
            console.error('User does not contain a document reference, failed to find one anyway');
          }
      }
    },

    // DELETE - delete user from FireStore and admin dashboard (not in use)
    async deleteUser({ state, commit }, user: MAFUser): Promise<any> {
        if (user.Reference) {
            try {
                return await user.Reference.delete();
            } catch (error) {
                console.log(error);
                return error;
            }
        } else {
            console.error('User does not contain a document reference.');
        }
    },

    // UPDATE - ban / unban user
    async setUserBan({ state, commit }, user: MAFUser): Promise<any> {
        if (user.Reference) {
            if (user.isBanned !== undefined) {
            try {
                // reverse whether a user is banned
                user.isBanned = !user.isBanned;
                return await user.Reference.update(user.toPlainObj());
            } catch (error) {
                console.log(error);
                return error;
            }
        } else {
            user.isBanned = true;
        }
        } else {
            console.error('User does not contain a document reference.');
        }
    },
    async setUserAdmin({ state, commit }, user: MAFUser): Promise<any> {
        if (user.Reference) {
            if (user.isAdmin !== undefined) {
                try {
                    // reverse whether a user is admin
                    user.isAdmin = !user.isAdmin;
                    return await user.Reference.update(user.toPlainObj());
                } catch (error) {
                    console.log(error);
                    return error;
                }
            } else {
                user.isAdmin = true;
            }
        } else {
            console.error('User does not contain a document reference.');
        }
    },

    async setUserBlock({ state, commit }, blockFriend): Promise<any> {
        const {isBlocked, user, friend} = blockFriend;

        if (state.self && state) {
            if (isBlocked) {
                // unblock the user
                user.blocked = user.blocked.filter((b: any) => b !== friend.uid);
                friend.blockingMe = friend.blockingMe.filter((b: any) => state && state.self && b !== state.self.uid);
            } else {
                // block the user
                user.blocked.push(friend.uid);
                friend.blockingMe.push(state.self.uid);
            }
        }

        if (state.selfRef) {
            try {
                await state.selfRef.update(user.toPlainObj());
            } catch (error) {
                console.error(`Error updating self: ${error}`);
            }
        }

        try {
            const friendToUpdate = Object.assign({}, friend);
            friendToUpdate.Reference = undefined;
            friendToUpdate.comparison = undefined;

            await friend.Reference.update(
                JSON.parse(JSON.stringify(friendToUpdate)),
            );
        } catch (error) {
            console.error(`Error updating friend: ${error}`);
        }

    },

    async setUserLocation({state, commit}, location: Position): Promise<any> {
        if (state.self && state.selfRef) {
            state.self.location = PositionObfuscator.generateRandomPoints(location, 16093);
            try {
                const body = state.self.toPlainObj();
                // @ts-ignore
                if (body.location && body.location.lat && body.location.long) {
                // @ts-ignore
                    body.location = new firebase.firestore.GeoPoint(body.location.lat, body.location.long);
                }
                await state.selfRef.update(body);
                return true;
            } catch (e) {
                console.error('Error updating self: ', e);
                return false;
            }
        }
    },

    // purchase audio or video using coins
    // what = audio or video (?)
    async purchase({commit}, {what}) {
        try {
            const purchase = Vue.prototype.$functions.httpsCallable('purchaseCoins');
            const res = await purchase({what});
            return {res};
        } catch (e) {
            return {err: e.message};
        }
    },

    /**
     * GETs
     * Below are functions to get certain information about a MAFUser
     */

    async getDiagnosis({commit}): Promise<any> {
        try {
            const doc: DocumentSnapshot = await Vue.prototype.$firestore.collection('lookups').doc('diagnosis').get();
            if (doc.exists) {
                doc.data();
                const lu: Lookup = new Lookup(doc.data());
                const done = _.after(lu.values ? lu.values.length : 0, () => {
                    commit('setDiagnosis', lu);
                });
                _.forEach(lu.values, async (opt) => {
                    await opt.getIcon();
                    done();
                });
            }
        } catch (e) {
            console.log('Error getting document:', e);
        }
    },

    async getDistances({commit}): Promise<any> {
        try {
            const doc: DocumentSnapshot =
                await Vue.prototype.$firestore.collection('lookups').doc('travel-distance').get();
            if (doc.exists) {
                commit('setDistances', doc.data());
            }
        } catch (e) {
            console.log('Error getting document:', e);
        }
    },

    getUserTransactions({commit}, {uid}): any {
        try {
            Vue.prototype.$firestore.
                collection(`users/${uid}/transactions`).onSnapshot((snapshot: QuerySnapshot | any ) => {

                const doc = UserTransaction.getListOfUserTransaction(snapshot);
                if (doc.length) {
                    commit('setTransactions', doc);
                }
            }, (error: any) => {
                if (Vue.prototype.$auth.currentUser !== null) {
                    console.log(`Error getting transactions: ${error}`);
                }
            });
        } catch (e) {
            console.log('Error getting document:', e);
        }
    },

    getPurchases({commit}) {
      Vue.prototype.$firestore.collection('lookups').doc('purchases').onSnapshot((snapshot: DocumentSnapshot) => {
          if (snapshot.exists) {
              commit('setPurchases', new Purchases(snapshot.data()));
          }
      }, (error: any) => {
        if (Vue.prototype.$auth.currentUser !== null) {
            console.log(`Error getting lookups: ${error}`);
        }
      });
    },

    async giftCoinsToSelf({state, commit}, {me, gift}): Promise<any> {
        me.coins = me.coins + gift;

        try {
          // update my coins
          const myDocumentRef = Vue.prototype.$firestore.collection('users').doc(me.uid);
          me.comparison = undefined;
          me.Reference = undefined;
          myDocumentRef.set(me.toPlainObj(), {merge: true});
        } catch (error) {
          console.error(error);
        }
      },

    async getUserType({commit}): Promise<any> {
        try {
            const doc: DocumentSnapshot = await Vue.prototype.$firestore.collection('lookups').doc('user-type').get();
            if (doc.exists) {
                commit('setUserType', doc.data());
            }
        } catch (e) {
            console.log('Error getting document:', e);
        }
    },

    async getGroupActivities({commit}): Promise<any> {
        try {
            const doc: DocumentSnapshot =
                await Vue.prototype.$firestore.collection('lookups').doc('group-fun-stuff').get();
            if (doc.exists) {
                doc.data();
                const lu: Lookup = new Lookup(doc.data());
                const done = _.after(lu.values ? lu.values.length : 0, () => {
                    commit('setGroupFunStuff', lu);
                });
                _.forEach(lu.values, async (opt) => {
                    await opt.getIcon();
                    done();
                });
            }
        } catch (e) {
            console.log('Error getting document:', e);
        }
    },

    async getActivities({commit}): Promise<any> {
        try {
            const doc: DocumentSnapshot = await Vue.prototype.$firestore.collection('lookups').doc('fun-stuff').get();
            if (doc.exists) {
                doc.data();
                const lu: Lookup = new Lookup(doc.data());
                const done = _.after(lu.values ? lu.values.length : 0, () => {
                    commit('setFunStuff', lu);
                });
                _.forEach(lu.values, async (opt) => {
                    await opt.getIcon();
                    done();
                });

            }
        } catch (e) {
            console.log('Error getting document:', e);
        }
    },
};
